import { FC, Fragment, ReactNode, useEffect, useState } from 'react';
import {
	Alert,
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Space,
	Table,
	Tabs,
	Upload,
	UploadFile,
} from 'antd';
import { ProspectFinancialReport } from '@/libs/fleet-api/services/types';
import { useForm } from '@/libs/antd/form';
import { useFRMPraSurveyInput } from './PraSurveyInputPage';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	DeleteOutlined,
	LoadingOutlined,
	EditOutlined,
	FileSearchOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import {
	useSelectFinancialReportAuditStatus,
	useSelectFinancialReportPeriodType,
	useSelectFinancialReportPeriodTypeDetail,
} from '@/libs/fleet-api/hooks/master-data';
import dayjs from 'dayjs';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import { useModals } from '@/components/modals/ModalProvider';
import {
	addFinancialReport,
	deleteFinancialReport,
	updateFinancialReport,
} from '@/libs/fleet-api/services/survey';
import { FleetFile } from '@/libs/fleet-api/fetcher';
import { useApp } from '@/App';
import { inputCurrency } from '@/libs/antd/input-number';
import replaceNullValue from '@/utils/replace-null-value';
import {
	calculateBusinessProfit,
	calculateEBITDA,
	calculateEquity,
	calculateGrossProfitAndLoss,
	calculateProfitLossBeforeTax,
	calculateProfitLossCurrentYear,
	calculateTotalAsset,
	calculateTotalCurrentAsset,
	calculateTotalLiability,
	calculateTotalLiabilityAndEquity,
	calculateTotalLongTermLiability,
	calculateTotalNonCurrentAsset,
	calculateTotalShortTermLiability,
} from '@/libs/antd/calculation';
import formRule from '@/libs/antd/rules';

type FRMPraSurveyInputFinancialReportFormProps = {
	onNext: () => void;
	onPrev: () => void;
	children?: ReactNode;
};

type FinancialReportItem = ProspectFinancialReport<'payload'> &
	Pick<
		ProspectFinancialReport,
		| 'auditStatus'
		| 'periodType'
		| 'periodTypeDetail'
		| 'financialReportFile'
		| 'financialReportFileData'
	>;

type FinancialReportForm = {
	input?: FinancialReportItem;
	items: FinancialReportItem[];
};

export const useFRMPraSurveyInputFinancialReportForm = () => {
	return useForm<FinancialReportForm>();
};

const FRMPraSurveyInputFinancialReportForm: FC<
	FRMPraSurveyInputFinancialReportFormProps
> = ({ children, onNext = () => false }) => {
	const { notification, modal: modalConfirm } = useApp();
	const { financialReportForm, mainInformation, prospectFinancialReports } =
		useFRMPraSurveyInput();
	const { openPreviewModal, openFinancialReportModal } = useModals();

	const { form, value, fieldName } = financialReportForm;
	const [modal, setModal] = useState<
		{ type: 'add' } | { type: 'edit'; index: number }
	>();
	const [activeTab, setActiveTab] = useState<string>('Neraca');

	const selectFinancialReportAuditStatus =
		useSelectFinancialReportAuditStatus();

	const selectFinancialReportPeriodType = useSelectFinancialReportPeriodType();
	const selectFinancialReportPeriodTypeDetail =
		useSelectFinancialReportPeriodTypeDetail(
			value?.input?.periodTypeId
				? {
						parentDataId: value.input.periodTypeId,
						parentSlug: 'financial-report-period-type',
					}
				: undefined,
		);

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const addFinancialReportMutation = useMutation({
		mutationFn: addFinancialReport,
	});

	const updateFinancialReportMutation = useMutation({
		mutationFn: updateFinancialReport,
	});

	const deleteFinancialReportMutation = useMutation({
		mutationFn: deleteFinancialReport,
	});

	const isLoading =
		addFinancialReportMutation.isPending ||
		updateFinancialReportMutation.isPending ||
		deleteFinancialReportMutation.isPending;

	// calculateTotalCurrentAsset when form value changed or form value is changed and set to totalAsset field
	useEffect(() => {
		if (value?.input) {
			const { balanceSheet, profitAndLoss } =
				value.input as ProspectFinancialReport<'payload'>;

			const totalCurrentAsset = calculateTotalCurrentAsset(value.input);
			const totalNonCurrentAsset = calculateTotalNonCurrentAsset(value.input);
			const totalShortTermLiability = calculateTotalShortTermLiability(
				value.input,
			);
			const totalLongTermLiability = calculateTotalLongTermLiability(
				value.input,
			);
			const totalLiability = calculateTotalLiability(value.input);
			const totalAsset = calculateTotalAsset(value.input);
			const equity = calculateEquity(value.input);
			const totalLiabilityAndEquity = calculateTotalLiabilityAndEquity(
				value.input,
			);

			const grossProfitLoss = calculateGrossProfitAndLoss(value.input);
			const ebitda = calculateEBITDA(value.input);
			const businessProfit = calculateBusinessProfit(value.input);
			const profitLossBeforeTax = calculateProfitLossBeforeTax(value.input);
			const profitLossTheYear = calculateProfitLossCurrentYear(value.input);

			form.setFieldsValue({
				input: {
					...value.input,
					balanceSheet: {
						...balanceSheet,
						totalCurrentAsset,
						totalNonCurrentAsset,
						totalShortTermLiability,
						totalLongTermLiability,
						totalAsset,
						totalLiability,
						equity,
						totalLiabilityAndEquity,
					},
					profitAndLoss: {
						...profitAndLoss,
						grossProfitLoss,
						ebitda,
						businessProfit,
						profitLossBeforeTax,
						profitLossTheYear,
					},
				},
			});
		}
	}, [form, value?.input]);

	const handleSubmit = async ({ input, items }: FinancialReportForm) => {
		const payload: ProspectFinancialReport<'payload'> = {
			prospectId: mainInformation!.prospectId,
			auditorName: input!.auditorName,
			auditStatusId: input!.auditStatusId,
			periodTypeId: input!.periodTypeId,
			periodTypeDetailId: input!.periodTypeDetailId,
			year: input!.year,
			financialReportFile: {
				id: input!.financialReportFile.id,
				name: input!.financialReportFile.name,
				path: input!.financialReportFile.path,
			},
			balanceSheet: replaceNullValue(input!.balanceSheet, 0),
			profitAndLoss: replaceNullValue(input!.profitAndLoss, 0),
		};

		if (modal?.type === 'edit') {
			form.setFieldsValue({
				items: items.map((item, index) => {
					return index === modal.index ? input! : item;
				}),
			});
			await updateFinancialReportMutation.mutateAsync({
				id: input!.id!,
				...payload,
			});
			setModal(undefined);
			setActiveTab('Neraca');
			form.resetFields([fieldName('input')]);
		} else {
			const res = await addFinancialReportMutation.mutateAsync(payload);
			form.setFieldsValue({
				items: [...items, { ...input!, id: res.data.id }],
			});
			setModal(undefined);
			setActiveTab('Neraca');
			form.resetFields([fieldName('input')]);
		}
	};

	const handleSubmitReport = () => {
		onNext();
	};

	const handleCloseModal = () => {
		setModal(undefined);
		setActiveTab('Neraca');
		form.resetFields([fieldName('input')]);
	};

	return (
		<Form.Provider>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				initialValues={
					{
						items:
							prospectFinancialReports?.map((report) => {
								return {
									id: report.id,
									auditorName: report.auditorName,
									auditStatus: report.auditStatus,
									auditStatusId: report.auditStatus.id,
									balanceSheet: report.balanceSheet,
									financialReportFile: report.financialReportFile,
									financialReportFileData: report.financialReportFile,
									periodType: report.periodType,
									periodTypeId: report.periodType.id,
									periodTypeDetail: report.periodTypeDetail,
									periodTypeDetailId: report.periodTypeDetail.id,
									profitAndLoss: report.profitAndLoss,
									prospectId: report.prospectId,
									year: report.year,
								};
							}) ?? [],
					} as FinancialReportForm
				}
				form={form}
				onFinish={handleSubmit}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['items'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue }) => {
						const values = getFieldsValue() as FinancialReportForm;
						const dataSource =
							values?.items?.map((item, key?) => {
								return { ...item, key };
							}) ?? [];

						return (
							<Space {...ANTD_DEFAULT_PROPS.SPACE}>
								{(value?.items?.length || 0) < 2 && (
									<Alert
										message="Disarankan untuk mengisi minimal 2 laporan keuangan agar Credit dapat melakukan komparasi"
										type="warning"
										className="custom-alert"
										showIcon
									/>
								)}
								<Table
									bordered
									dataSource={dataSource}
									loading={isLoading}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											width: 1,
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	openFinancialReportModal(
																		record as ProspectFinancialReport,
																	);
																}}
															>
																<FileSearchOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setModal({
																		type: 'edit',
																		index,
																	});
																	setFieldValue(fieldName(['input']), record);
																}}
															>
																<EditOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	modalConfirm.confirm({
																		title: 'Delete',
																		content: 'Delete this report?',
																		onOk: async () => {
																			if (record.id) {
																				await deleteFinancialReportMutation.mutateAsync(
																					record.id,
																				);
																			}

																			form.setFieldsValue({
																				items: dataSource
																					.filter((item) => {
																						return item.key !== index;
																					})
																					.map((item) => {
																						delete item.key;
																						return item;
																					}),
																			});
																		},
																	});
																}}
															>
																<DeleteOutlined />
															</a>
														</div>
													</Row>
												);
											},
										},
										{
											title: 'Laporan Keuangan',
											dataIndex: ['auditStatus', 'value'],
										},
										{
											title: 'Nama Auditor',
											dataIndex: ['auditorName'],
										},
										{
											title: 'Periode',
											dataIndex: ['periodType', 'value'],
										},
										{
											title: 'Detail Periode',
											dataIndex: ['periodTypeDetail', 'value'],
										},
										{
											title: 'Tahun',
											dataIndex: ['year'],
										},
										{
											title: 'View Dokumen',
											render: (_, record) => (
												<a
													onClick={() => {
														if (!record.financialReportFile) return;
														openPreviewModal(
															record.financialReportFile as FleetFile,
														);
													}}
												>
													View Dokumen
												</a>
											),
										},
									]}
									pagination={false}
								/>
								{children}
								<Space
									style={{ display: 'flex', justifyContent: 'end' }}
									size="large"
								>
									{/* <Button
										style={{
											width: 120,
											display: 'block',
										}}
										size="small"
										color="secondary"
										loading={isLoading}
										onClick={onPrev}
									>
										Back
									</Button> */}
									<Space
										style={{ display: 'flex', justifyContent: 'end', flex: 1 }}
										size="middle"
									>
										<Button
											style={{
												width: 120,
												display: 'block',
											}}
											loading={isLoading}
											onClick={() => setModal({ type: 'add' })}
										>
											ADD
										</Button>
										<Button
											type="primary"
											style={{
												width: 200,
												display: 'block',
											}}
											disabled={(value?.items?.length || 0) < 1}
											onClick={handleSubmitReport}
										>
											NEXT
										</Button>
									</Space>
								</Space>

								<FleetModal
									open={Boolean(modal)}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									styles={{
										footer: {
											display: 'none',
										},
									}}
								>
									<FleetTitle>Add Laporan Keuangan</FleetTitle>
									<Form.Item
										name={fieldName(['input', 'id'])}
										noStyle
									/>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'auditStatus'])}
												noStyle
											/>
											<Form.Item
												label="Laporan Keuangan"
												name={fieldName(['input', 'auditStatusId'])}
												labelCol={{ span: 10 }}
												rules={[formRule.required()]}
											>
												<Select
													{...selectFinancialReportAuditStatus}
													options={
														!values.input?.auditStatus ||
														selectFinancialReportAuditStatus.options?.length
															? selectFinancialReportAuditStatus.options
															: [
																	{
																		label: values.input.auditStatus.value,
																		value: values.input.auditStatus.id,
																	},
																]
													}
													onSelect={(value, option) => {
														selectFinancialReportAuditStatus.onSelect!(
															value,
															option,
														);
														setFieldValue(
															fieldName(['input', 'auditStatus']),
															option.data,
														);
													}}
												/>
											</Form.Item>
										</Col>
										{values?.input?.auditStatus?.id === 1 && (
											<Col span={12}>
												<Form.Item
													label="Nama Auditor"
													name={fieldName(['input', 'auditorName'])}
													labelCol={{ span: 10 }}
													rules={[formRule.required()]}
												>
													<Input />
												</Form.Item>
											</Col>
										)}
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'periodType'])}
												noStyle
											/>
											<Form.Item
												label="Periode"
												name={fieldName(['input', 'periodTypeId'])}
												labelCol={{ span: 10 }}
												rules={[formRule.required()]}
											>
												<Select
													{...selectFinancialReportPeriodType}
													options={
														!values.input?.periodType ||
														selectFinancialReportPeriodType.options?.length
															? selectFinancialReportPeriodType.options
															: [
																	{
																		label: values.input.periodType.value,
																		value: values.input.periodType.id,
																	},
																]
													}
													onSelect={(value, option) => {
														selectFinancialReportPeriodType.onSelect!(
															value,
															option,
														);
														setFieldValue(
															fieldName(['input', 'periodType']),
															option.data,
														);
													}}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'periodTypeDetail'])}
												noStyle
											/>
											<Form.Item
												label="Detail Periode"
												name={fieldName(['input', 'periodTypeDetailId'])}
												labelCol={{ span: 10 }}
												rules={[formRule.required()]}
											>
												<Select
													{...selectFinancialReportPeriodTypeDetail}
													options={
														!values.input?.periodTypeDetail ||
														selectFinancialReportPeriodTypeDetail.options
															?.length
															? selectFinancialReportPeriodTypeDetail.options
															: [
																	{
																		label: values.input.periodTypeDetail.value,
																		value: values.input.periodTypeDetail.id,
																	},
																]
													}
													onSelect={(value, option) => {
														selectFinancialReportPeriodTypeDetail.onSelect!(
															value,
															option,
														);
														setFieldValue(
															fieldName(['input', 'periodTypeDetail']),
															option.data,
														);
													}}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Tahun"
												name={fieldName(['input', 'year'])}
												labelCol={{ span: 10 }}
												getValueProps={(value) => ({
													value: value && dayjs().set('year', value),
												})}
												normalize={(value) =>
													value && Number(dayjs(value).format('YYYY'))
												}
												rules={[formRule.required()]}
											>
												<DatePicker.YearPicker />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'financialReportFile'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'financialReportFileData'])}
												label="Upload Laporan Keuangan"
												labelCol={{ span: 10 }}
												rules={[formRule.required()]}
											>
												<Upload.Dragger
													listType="picture"
													accept=".pdf"
													maxCount={1}
													onRemove={() => {
														form.setFieldValue(
															fieldName(['input', 'financialReportFile']),
															null,
														);
														return false;
													}}
													fileList={
														values.input?.financialReportFile
															? [
																	{
																		name: values.input.financialReportFile.name,
																		uid: values.input.financialReportFile.id,
																		url: values.input.financialReportFile.url,
																	} as UploadFile,
																]
															: []
													}
													beforeUpload={async (file) => {
														const fileid = Date.now().toString();
														const filepath = `customer/${mainInformation!.customerId}/${mainInformation!.prospectId}/financial-reports`;

														try {
															const url = await uploadMutation.mutateAsync({
																file: file as unknown as File,
																fileid,
																filepath,
															});

															form.setFieldValue(
																fieldName(['input', 'financialReportFile']),
																{
																	format: 'pdf',
																	id: fileid,
																	name: file.name,
																	path: filepath,
																	url,
																},
															);
														} catch {
															form.setFieldValue(
																fieldName(['input', 'financialReportFile']),
																null,
															);
														}
														return false;
													}}
												>
													{uploadMutation.isPending ? (
														<LoadingOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													) : (
														<UploadOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													)}
												</Upload.Dragger>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Tabs
										activeKey={activeTab}
										onChange={(key) => setActiveTab(key)}
										items={[
											{
												key: 'Neraca',
												label: 'Neraca',
												children: (
													<Fragment>
														<Row gutter={[40, 20]}>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'cash',
																	])}
																	label="Kas Dan Setara Kas"
																	labelCol={{ span: 10 }}
																	rules={[formRule.required()]}
																>
																	<InputNumber {...inputCurrency} />
																</Form.Item>
															</Col>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'shortTermLoan',
																	])}
																	label="Pinjaman Jangka Pendek"
																	labelCol={{ span: 10 }}
																	rules={[formRule.required()]}
																>
																	<InputNumber {...inputCurrency} />
																</Form.Item>
															</Col>
														</Row>
														<Divider />
														<Row gutter={[40, 20]}>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'financingReceivableCurrentAsset',
																	])}
																	label="Piutang Usaha Pembiayaan Aset Lancar"
																	labelCol={{ span: 10 }}
																	rules={[formRule.required()]}
																>
																	<InputNumber {...inputCurrency} />
																</Form.Item>
															</Col>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'shortTermAccountPayable',
																	])}
																	label="Utang Usaha Jangka Pendek"
																	labelCol={{ span: 10 }}
																	rules={[formRule.required()]}
																>
																	<InputNumber {...inputCurrency} />
																</Form.Item>
															</Col>
														</Row>
														<Divider />
														<Row gutter={[40, 20]}>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'otherFinancialAssetInvestmentCurrentAsset',
																	])}
																	label="Investasi Aset Keuangan Lainnya Aset Lancar"
																	labelCol={{ span: 10 }}
																	rules={[formRule.required()]}
																>
																	<InputNumber {...inputCurrency} />
																</Form.Item>
															</Col>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'otherShortTermLiability',
																	])}
																	label="Liabilitas Jangka Pendek Lainnya"
																	labelCol={{ span: 10 }}
																	rules={[formRule.required()]}
																>
																	<InputNumber {...inputCurrency} />
																</Form.Item>
															</Col>
														</Row>
														<Divider />
														<Row gutter={[40, 20]}>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'otherCurrentAsset',
																	])}
																	label="Aset Lancar Lainnya"
																	labelCol={{ span: 10 }}
																	rules={[formRule.required()]}
																>
																	<InputNumber {...inputCurrency} />
																</Form.Item>
															</Col>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'totalShortTermLiability',
																	])}
																	label="Total Liabilitas Jangka Pendek"
																	labelCol={{ span: 10 }}
																>
																	<InputNumber
																		{...inputCurrency}
																		disabled
																	/>
																</Form.Item>
															</Col>
														</Row>
														<Divider />
														<Row gutter={[40, 20]}>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'totalCurrentAsset',
																	])}
																	label="Total Aset Lancar"
																	labelCol={{ span: 10 }}
																>
																	<InputNumber
																		{...inputCurrency}
																		disabled
																	/>
																</Form.Item>
															</Col>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'longTermLoan',
																	])}
																	label="Pinjaman Jangka Panjang"
																	labelCol={{ span: 10 }}
																	rules={[formRule.required()]}
																>
																	<InputNumber {...inputCurrency} />
																</Form.Item>
															</Col>
														</Row>
														<Divider />
														<Row gutter={[40, 20]}>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'financingReceivableNonCurrentAsset',
																	])}
																	label="Piutang Usaha Pembiayaan Aset Tidak Lancar"
																	labelCol={{ span: 10 }}
																	rules={[formRule.required()]}
																>
																	<InputNumber {...inputCurrency} />
																</Form.Item>
															</Col>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'longTermAccountPayable',
																	])}
																	label="Utang Usaha Jangka Panjang"
																	labelCol={{ span: 10 }}
																	rules={[formRule.required()]}
																>
																	<InputNumber {...inputCurrency} />
																</Form.Item>
															</Col>
														</Row>
														<Divider />
														<Row gutter={[40, 20]}>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'otherFinancialAssetInvestmentNonCurrentAsset',
																	])}
																	label="Investasi Aset Keuangan Lainnnya Aset Tidak Lancar"
																	labelCol={{ span: 10 }}
																	rules={[formRule.required()]}
																>
																	<InputNumber {...inputCurrency} />
																</Form.Item>
															</Col>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'otherLongTermLiability',
																	])}
																	label="Liabilitas Jangka Panjang Lainnya"
																	labelCol={{ span: 10 }}
																	rules={[formRule.required()]}
																>
																	<InputNumber {...inputCurrency} />
																</Form.Item>
															</Col>
														</Row>
														<Divider />
														<Row gutter={[40, 20]}>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'otherNonCurrentAsset',
																	])}
																	label="Aset Tidak Lancar Lainnya"
																	labelCol={{ span: 10 }}
																	rules={[formRule.required()]}
																>
																	<InputNumber {...inputCurrency} />
																</Form.Item>
															</Col>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'totalLongTermLiability',
																	])}
																	label="Total Liabilitas Jangka Panjang"
																	labelCol={{ span: 10 }}
																>
																	<InputNumber
																		{...inputCurrency}
																		disabled
																	/>
																</Form.Item>
															</Col>
														</Row>
														<Divider />
														<Row gutter={[40, 20]}>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'totalNonCurrentAsset',
																	])}
																	label="Total Aset Tidak Lancar"
																	labelCol={{ span: 10 }}
																>
																	<InputNumber
																		{...inputCurrency}
																		disabled
																	/>
																</Form.Item>
															</Col>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'equity',
																	])}
																	label="Ekuitas"
																	labelCol={{ span: 10 }}
																>
																	<InputNumber
																		{...inputCurrency}
																		disabled
																	/>
																</Form.Item>
															</Col>
														</Row>
														<Divider />
														<Row gutter={[40, 20]}>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'totalAsset',
																	])}
																	label={
																		<span style={{ color: '#1557BC' }}>
																			TOTAL ASET
																		</span>
																	}
																	labelCol={{
																		span: 10,
																		style: {
																			color: '#1557BC',
																		},
																	}}
																>
																	<InputNumber
																		{...inputCurrency}
																		disabled
																	/>
																</Form.Item>
															</Col>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'totalLiability',
																	])}
																	label={
																		<span style={{ color: '#1557BC' }}>
																			TOTAL LIABILITAS
																		</span>
																	}
																	labelCol={{ span: 10 }}
																>
																	<InputNumber
																		{...inputCurrency}
																		disabled
																	/>
																</Form.Item>
															</Col>
														</Row>
														<Divider />
														<Row gutter={[40, 20]}>
															<Col span={12}></Col>
															<Col span={12}>
																<Form.Item
																	name={fieldName([
																		'input',
																		'balanceSheet',
																		'totalLiabilityAndEquity',
																	])}
																	label={
																		<span style={{ color: '#1557BC' }}>
																			TOTAL LIABILITAS & EKUITAS
																		</span>
																	}
																	labelCol={{ span: 10 }}
																>
																	<InputNumber
																		{...inputCurrency}
																		disabled
																	/>
																</Form.Item>
															</Col>
														</Row>
														<Divider />
														<Space
															style={{
																display: 'flex',
																justifyContent: 'flex-end',
															}}
															size="large"
														>
															<Button
																style={{
																	width: 200,
																	display: 'block',
																}}
																onClick={() => handleCloseModal()}
															>
																CANCEL
															</Button>
															<Button
																type="primary"
																style={{
																	width: 200,
																	display: 'block',
																}}
																onClick={async () => {
																	// validate only balance sheet on first tab
																	await form
																		.validateFields(
																			[['input', 'balanceSheet']],
																			{ recursive: true },
																		)
																		.then(() => {
																			setActiveTab('Laba Rugi');
																		})
																		.catch(() => {
																			notification.error({
																				message: 'Validasi Gagal',
																				description:
																					'Belum semua field diisi, mohon periksa kembali dan lengkapi field yang Wajib diisi',
																			});
																		});
																}}
															>
																NEXT
															</Button>
														</Space>
													</Fragment>
												),
											},
											{
												key: 'Laba Rugi',
												label: 'Laba Rugi',
												children: (
													<Fragment>
														<Form.Item
															name={fieldName([
																'input',
																'profitAndLoss',
																'operatingBusinessIncome',
															])}
															label="Pendapatan Usaha Operasional"
															labelCol={{ span: 12 }}
															rules={[formRule.required()]}
														>
															<InputNumber {...inputCurrency} />
														</Form.Item>
														<Divider />
														<Form.Item
															name={fieldName([
																'input',
																'profitAndLoss',
																'costOfRevenue',
															])}
															label="Beban Pokok Pendapatan"
															labelCol={{ span: 12 }}
															rules={[formRule.required()]}
														>
															<InputNumber {...inputCurrency} />
														</Form.Item>
														<Divider />
														<Form.Item
															name={fieldName([
																'input',
																'profitAndLoss',
																'grossProfitLoss',
															])}
															label="Laba Rugi Bruto"
															labelCol={{ span: 12 }}
														>
															<InputNumber
																{...inputCurrency}
																disabled
															/>
														</Form.Item>
														<Divider />
														<Form.Item
															name={fieldName([
																'input',
																'profitAndLoss',
																'costOfMarketingAndAdmin',
															])}
															label="Biaya Pemasaran Umum dan Administrasi"
															labelCol={{ span: 12 }}
															rules={[formRule.required()]}
														>
															<InputNumber {...inputCurrency} />
														</Form.Item>
														<Divider />
														<Form.Item
															name={fieldName([
																'input',
																'profitAndLoss',
																'ebitda',
															])}
															label="EBITDA"
															labelCol={{ span: 12 }}
														>
															<InputNumber
																{...inputCurrency}
																disabled
															/>
														</Form.Item>
														<Divider />
														<Form.Item
															name={fieldName([
																'input',
																'profitAndLoss',
																'costOfDepreciationAndAmortization',
															])}
															label="Biaya Depresiasi dan Amortisasi"
															labelCol={{ span: 12 }}
															rules={[formRule.required()]}
														>
															<InputNumber {...inputCurrency} />
														</Form.Item>
														<Divider />
														<Form.Item
															name={fieldName([
																'input',
																'profitAndLoss',
																'businessProfit',
															])}
															label="Laba Usaha"
															labelCol={{ span: 12 }}
														>
															<InputNumber
																{...inputCurrency}
																disabled
															/>
														</Form.Item>

														<Divider />

														<Form.Item
															name={fieldName([
																'input',
																'profitAndLoss',
																'otherIncome',
															])}
															label="Pendapatan Lain-Lain"
															labelCol={{ span: 12 }}
															rules={[formRule.required()]}
														>
															<InputNumber {...inputCurrency} />
														</Form.Item>

														<Divider />

														<Form.Item
															name={fieldName([
																'input',
																'profitAndLoss',
																'otherExpense',
															])}
															label="Beban Lain-Lain"
															labelCol={{ span: 12 }}
															rules={[formRule.required()]}
														>
															<InputNumber {...inputCurrency} />
														</Form.Item>
														<Divider />
														<Form.Item
															name={fieldName([
																'input',
																'profitAndLoss',
																'profitLossBeforeTax',
															])}
															label="Laba Rugi Sebelum Pajak"
															labelCol={{ span: 12 }}
														>
															<InputNumber
																{...inputCurrency}
																disabled
															/>
														</Form.Item>
														<Divider />
														<Form.Item
															name={fieldName([
																'input',
																'profitAndLoss',
																'taxBenefitOrCost',
															])}
															label="Manfaat atau Beban Pajak"
															labelCol={{ span: 12 }}
															rules={[formRule.required()]}
														>
															<InputNumber {...inputCurrency} />
														</Form.Item>
														<Divider />

														<Form.Item
															name={fieldName([
																'input',
																'profitAndLoss',
																'profitLossTheYear',
															])}
															label="Laba Rugi Tahun Berjalan"
															labelCol={{ span: 12 }}
														>
															<InputNumber
																{...inputCurrency}
																disabled
															/>
														</Form.Item>
														<Divider />

														<Space
															style={{
																display: 'flex',
																justifyContent: 'flex-end',
															}}
															size="large"
														>
															<Button
																style={{
																	width: 200,
																	display: 'block',
																}}
																onClick={() => handleCloseModal()}
															>
																CANCEL
															</Button>
															<Button
																type="primary"
																style={{
																	width: 200,
																	display: 'block',
																	marginLeft: 'auto',
																}}
																onClick={async () => {
																	// validate all fields on second tab
																	await form
																		.validateFields()
																		.then(() => {
																			form.submit();
																		})
																		.catch(() => {
																			notification.error({
																				message: 'Validasi Gagal',
																				description:
																					'Belum semua field diisi, mohon periksa kembali dan lengkapi field yang Wajib diisi',
																			});
																		});
																}}
															>
																SAVE
															</Button>
														</Space>
													</Fragment>
												),
											},
										]}
									/>
								</FleetModal>
							</Space>
						);
					}}
				</Form.Item>
			</Form>
		</Form.Provider>
	);
};

export default FRMPraSurveyInputFinancialReportForm;
